import type { WorkflowNodeKind } from "./workflow-graph";
import { WORKFLOW_TRIGGER_CATALOG, type EventBackedTriggerType } from "./workflow-trigger-catalog";

const NON_EVENT_TRIGGER_TYPES = ["manual", "schedule"] as const;
type NonEventTriggerType = (typeof NON_EVENT_TRIGGER_TYPES)[number];
export type TriggerType = NonEventTriggerType | EventBackedTriggerType;
export const TRIGGER_TYPES: readonly TriggerType[] = [
  ...NON_EVENT_TRIGGER_TYPES,
  ...(Object.keys(WORKFLOW_TRIGGER_CATALOG) as EventBackedTriggerType[]),
];

/**
 * The platform events that start each trigger type, through the one
 * `workflowTriggers` bus subscriber. `manual` and `schedule` are started by a
 * person and by the schedules poller. A new trigger type gets a row here and an
 * event in `PLATFORM_EVENTS`; it does not get a job or a fan-out of its own.
 */
export const BUS_TRIGGER_EVENTS = Object.fromEntries(
  Object.entries(WORKFLOW_TRIGGER_CATALOG).map(([triggerType, definition]) => [
    triggerType,
    definition.eventTypes,
  ]),
) as Readonly<Record<EventBackedTriggerType, readonly string[]>>;

export type { BusTriggerEvent } from "./workflow-trigger-catalog";

/** Trigger types the editor offers that nothing starts yet. Wiring one moves it to `BUS_TRIGGER_EVENTS`. */
export const UNWIRED_TRIGGER_TYPES = [] as const satisfies readonly TriggerType[];

export const CONDITION_TYPES = ["field_equals", "placeholder", "else_branch"] as const;
export type ConditionType = (typeof CONDITION_TYPES)[number];

export const ACTION_TYPES = [
  "run_classifier",
  "run_extractor",
  "human_review",
  "send_email",
  "webhook",
  "create_task",
  "run_agent",
  "add_tag",
  "remove_tag",
] as const;
export type ActionType = (typeof ACTION_TYPES)[number];

export type WorkflowNodeType = TriggerType | ConditionType | ActionType;
export type EntityRefKind = "classifier" | "extractor" | "agent";

export type ActionConfigRequirement = {
  requiredFields: string[];
  entityRefs?: Partial<Record<string, EntityRefKind>>;
  placeable: boolean;
  implemented: boolean;
};

/**
 * Editor `node.type` → worker step type (the registry key in
 * `apps/worker/src/workflow-steps.ts`). Promoted out of the worker
 * itself so the web UI can invert it (step → editor type) when it
 * needs to look up a contract for a running step.
 *
 * Conditions and triggers don't get an entry — they don't dispatch to
 * the step registry. Add an entry when introducing a new action type.
 */
export const ACTION_STEP_TYPE_MAP: Record<ActionType, string> = {
  run_classifier: "RUN_CLASSIFIER",
  run_extractor: "RUN_EXTRACTION",
  human_review: "HUMAN_REVIEW",
  send_email: "SEND_EMAIL",
  webhook: "WEBHOOK",
  create_task: "CREATE_TASK",
  run_agent: "RUN_AGENT",
  add_tag: "ADD_TAG",
  remove_tag: "REMOVE_TAG",
};

const STEP_TYPE_TO_ACTION = new Map<string, ActionType>(
  (Object.entries(ACTION_STEP_TYPE_MAP) as Array<[ActionType, string]>).map(([action, step]) => [
    step,
    action,
  ]),
);

/**
 * Inverse of {@link ACTION_STEP_TYPE_MAP}. Given a step type recorded
 * on a `workflow_run_steps` row, return the editor's node type so
 * callers can look up the typed contract for it. Returns `undefined`
 * for unmapped strings (legacy / test step types).
 */
export function actionTypeFromStepType(stepType: string): ActionType | undefined {
  return STEP_TYPE_TO_ACTION.get(stepType);
}

export const ACTION_CONFIG_REQUIREMENTS: Record<ActionType, ActionConfigRequirement> = {
  run_classifier: {
    requiredFields: ["classifierId"],
    entityRefs: { classifierId: "classifier" },
    placeable: true,
    implemented: false,
  },
  run_extractor: {
    requiredFields: ["processorPublicId"],
    entityRefs: { processorPublicId: "extractor" },
    placeable: true,
    implemented: true,
  },
  human_review: {
    // Config validation is delegated to the Zod contract in
    // `node-contracts/human-review.ts` (which enforces "either inline
    // questions or a questionsFrom selector"). Keeping the legacy
    // requiredFields list empty avoids duplicate / conflicting checks.
    requiredFields: [],
    placeable: true,
    implemented: true,
  },
  send_email: {
    // Publish validation runs through the registered `send_email` node
    // contract (packages/types/src/node-contracts/send-email.ts), whose
    // superRefine expresses the cross-field rule this flat list can't: a
    // recipient is required from EITHER a static/templated `to` OR a
    // `replyTo` block, and `subject` is required unless replying. These
    // `requiredFields` are a fallback only used when no contract is
    // registered, so they're kept in sync but no longer the source of truth.
    requiredFields: ["to", "subject"],
    placeable: true,
    implemented: true,
  },
  webhook: {
    requiredFields: [],
    placeable: true,
    implemented: false,
  },
  create_task: {
    requiredFields: [],
    placeable: true,
    implemented: true,
  },
  run_agent: {
    // Publish validation runs through the registered `run_agent` node
    // contract (packages/types/src/node-contracts/run-agent.ts), which
    // requires a selected agent. This fallback list is only consulted when
    // no contract is registered — kept in sync but not the source of truth.
    // The entityRefs entry gives draft/publish validation an existence check
    // on the referenced agent, so a deleted agent surfaces at publish time
    // rather than as a run-time step failure.
    requiredFields: ["agentPublicId"],
    entityRefs: { agentPublicId: "agent" },
    placeable: true,
    implemented: true,
  },
  add_tag: {
    requiredFields: ["tag", "resource"],
    placeable: true,
    implemented: true,
  },
  remove_tag: {
    requiredFields: ["tag", "resource"],
    placeable: true,
    implemented: true,
  },
};

const PLACEABLE_TRIGGER_TYPES: readonly TriggerType[] = TRIGGER_TYPES;
const PLACEABLE_CONDITION_TYPES = ["field_equals"] as const satisfies readonly ConditionType[];
const PLACEABLE_ACTION_TYPES = ACTION_TYPES.filter(
  (type) => ACTION_CONFIG_REQUIREMENTS[type].placeable,
) as ActionType[];
const PUBLISHABLE_CONDITION_TYPES = [
  "field_equals",
  "else_branch",
] as const satisfies readonly ConditionType[];

const NODE_TYPE_SETS = {
  trigger: new Set<string>(TRIGGER_TYPES),
  condition: new Set<string>(CONDITION_TYPES),
  action: new Set<string>(ACTION_TYPES),
} satisfies Record<WorkflowNodeKind, Set<string>>;

const PLACEABLE_NODE_TYPE_SETS = {
  trigger: new Set<string>(PLACEABLE_TRIGGER_TYPES),
  condition: new Set<string>(PLACEABLE_CONDITION_TYPES),
  action: new Set<string>(PLACEABLE_ACTION_TYPES),
} satisfies Record<WorkflowNodeKind, Set<string>>;

const PUBLISHABLE_NODE_TYPE_SETS = {
  trigger: new Set<string>(TRIGGER_TYPES),
  condition: new Set<string>(PUBLISHABLE_CONDITION_TYPES),
  action: new Set<string>(ACTION_TYPES),
} satisfies Record<WorkflowNodeKind, Set<string>>;

export function isValidNodeType(kind: WorkflowNodeKind, type: string): boolean {
  return NODE_TYPE_SETS[kind].has(type);
}

export function isPlaceableNodeType(kind: WorkflowNodeKind, type: string): boolean {
  return PLACEABLE_NODE_TYPE_SETS[kind].has(type);
}

export function isPublishableNodeType(kind: WorkflowNodeKind, type: string): boolean {
  return PUBLISHABLE_NODE_TYPE_SETS[kind].has(type);
}

/**
 * Whether a node type has a real runtime handler. Triggers and conditions
 * don't dispatch to the step registry (they're graph entry points / inline
 * routing), so they're always considered implemented. Actions carry an
 * explicit `implemented` flag in {@link ACTION_CONFIG_REQUIREMENTS}; an
 * action that is placeable but not yet implemented (e.g. `send_email`,
 * `webhook`) is shown in the picker as "coming soon".
 */
export function isImplementedNodeType(kind: WorkflowNodeKind, type: string): boolean {
  if (kind !== "action") return true;
  const requirement = ACTION_CONFIG_REQUIREMENTS[type as ActionType];
  return requirement?.implemented ?? false;
}
